'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff, Loader2, X, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type AuthMode = 'login' | 'signup';
type AccountType = 'homeowner' | 'provider';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signUp, user, loading: authLoading } = useAuth();

  const [mode, setMode] = useState<AuthMode>((searchParams.get('mode') as AuthMode) || 'login');
  const [accountType, setAccountType] = useState<AccountType>(
    (searchParams.get('type') as AccountType) || 'homeowner'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const nextPath = searchParams.get('next') ?? (accountType === 'provider' ? '/provider/dashboard' : '/homepage');

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      router.replace(nextPath);
    }
  }, [user, authLoading, router, nextPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (mode === 'signup' && !fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        router.replace(nextPath);
        router.refresh();
      } else {
        await signUp(email, password, {
          fullName,
          accountType,
        });
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-love-plum-light" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex flex-col items-center text-center py-8 gap-4">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#99E6C818' }}>
          <CheckCircle2 size={32} style={{ color: '#059669' }} />
        </div>
        <div>
          <p className="text-xl font-bold text-gray-900 font-display">Account created!</p>
          <p className="text-sm text-gray-500 mt-2 max-w-xs">
            Check your email to confirm your account, then log in to get started.
          </p>
        </div>
        <button
          onClick={() => { setSuccess(false); setMode('login'); setPassword(''); }}
          className="mt-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-150 active:scale-95"
          style={{ backgroundColor: '#3A2047' }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Mode tabs */}
      <div className="flex rounded-xl border border-gray-200 p-1 mb-6 bg-gray-50">
        {(['login', 'signup'] as AuthMode[]).map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
              mode === m ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {m === 'login' ? 'Log in' : 'Sign up'}
          </button>
        ))}
      </div>

      {/* Account type selector (signup only) */}
      {mode === 'signup' && (
        <div className="flex rounded-xl border border-gray-200 p-1 mb-5 bg-gray-50">
          {(['homeowner', 'provider'] as AccountType[]).map((t) => (
            <button
              key={t}
              onClick={() => setAccountType(t)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                accountType === t ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'homeowner' ? '🏠 Homeowner' : '🔧 Trade Provider'}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl mb-4 text-sm" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
          <X size={15} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'signup' && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5" htmlFor="full-name">
              Full name <span className="text-red-400">*</span>
            </label>
            <input
              id="full-name"
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Smith"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5" htmlFor="email">
            Email address <span className="text-red-400">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-gray-600" htmlFor="password">
              Password <span className="text-red-400">*</span>
            </label>
            {mode === 'login' && (
              <span className="text-xs text-love-plum-light cursor-pointer hover:underline">
                Forgot password?
              </span>
            )}
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'signup' ? 'Min. 6 characters' : '••••••••'}
              className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-love-plum-light transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed mt-2 text-white"
          style={{ backgroundColor: '#3A2047' }}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {mode === 'login' ? 'Signing in…' : 'Creating account…'}
            </>
          ) : (
            mode === 'login' ? 'Log in' : 'Create account'
          )}
        </button>
      </form>

      <p className="text-center text-xs text-gray-400 mt-5">
        {mode === 'login' ? (
          <>Don't have an account?{' '}
            <button onClick={() => { setMode('signup'); setError(''); }} className="text-love-plum-light font-medium hover:underline">
              Sign up free
            </button>
          </>
        ) : (
          <>Already have an account?{' '}
            <button onClick={() => { setMode('login'); setError(''); }} className="text-love-plum-light font-medium hover:underline">
              Log in
            </button>
          </>
        )}
      </p>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#F8F5FA' }}>
      <div className="w-full max-w-md">
        {/* Back link */}
        <Link
          href="/homepage"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft size={15} />
          Back to LoveLocals
        </Link>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3"
              style={{ background: 'linear-gradient(135deg, #250C31 0%, #3A2047 100%)' }}
            >
              <span className="text-white text-xl">❤️</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 font-display">Welcome to LoveLocals</h1>
            <p className="text-sm text-gray-500 mt-1">Connecting homeowners with trusted local trades</p>
          </div>

          <Suspense fallback={<div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-400" /></div>}>
            <LoginForm />
          </Suspense>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          By continuing, you agree to our{' '}
          <span className="text-love-plum-light cursor-pointer hover:underline">Terms of Service</span>
          {' '}and{' '}
          <span className="text-love-plum-light cursor-pointer hover:underline">Privacy Policy</span>
        </p>
      </div>
    </div>
  );
}
